// ================================================================
// 管理員認證上下文
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：管理員登入狀態管理
// ================================================================

import React, { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';
import { Admin, adminLogin, AdminLoginRequest } from '../api/admin';

// ================================================================
// 類型定義
// ================================================================

export interface AdminContextType {
  isAdminAuthenticated: boolean;
  isLoading: boolean;
  admin: Admin | null;
  adminToken: string | null;
  login: (data: AdminLoginRequest) => Promise<void>;
  logout: () => void;
}

// ================================================================
// 建立上下文
// ================================================================

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// ================================================================
// Provider Props
// ================================================================

interface AdminProviderProps {
  children: ReactNode;
}

// ================================================================
// 管理員認證提供者
// ================================================================

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [adminToken, setAdminToken] = useState<string | null>(null);

  // 初始化：從 localStorage 載入
  useEffect(() => {
    const initializeAdmin = async () => {
      try {
        const storedToken = localStorage.getItem('admin_token');
        const storedAdmin = localStorage.getItem('admin_info');

        if (storedToken && storedAdmin) {
          setAdminToken(storedToken);
          setAdmin(JSON.parse(storedAdmin));
          setIsAdminAuthenticated(true);
        }
      } catch (error) {
        console.error('管理員認證初始化失敗:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_info');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAdmin();
  }, []);

  // 登入
  const login = useCallback(async (data: AdminLoginRequest) => {
    const response = await adminLogin(data);

    if (!response.success || !response.token || !response.admin) {
      throw new Error(response.message || '登入失敗');
    }

    // 儲存到 state 和 localStorage
    setAdminToken(response.token);
    setAdmin(response.admin);
    setIsAdminAuthenticated(true);

    localStorage.setItem('admin_token', response.token);
    localStorage.setItem('admin_info', JSON.stringify(response.admin));
  }, []);

  // 登出
  const logout = useCallback(() => {
    setAdminToken(null);
    setAdmin(null);
    setIsAdminAuthenticated(false);

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_info');
  }, []);

  const value: AdminContextType = {
    isAdminAuthenticated,
    isLoading,
    admin,
    adminToken,
    login,
    logout,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

// ================================================================
// Custom Hook
// ================================================================

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export { AdminContext };
